
import { useState, useEffect } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";

interface IconSelectorProps {
  selectedIcon: string | null;
  onSelectIcon: (iconPath: string) => void;
}

export function IconSelector({ selectedIcon, onSelectIcon }: IconSelectorProps) {
  const [icons, setIcons] = useState<string[]>([]);
  const [techIcons, setTechIcons] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isTechLoading, setIsTechLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchSimple, setSearchSimple] = useState("");
  const [searchTech, setSearchTech] = useState("");

  useEffect(() => {
    const fetchIcons = async () => {
      try {
        setIsLoading(true);
        console.log("Buscando ícones locais...");
        
        // Lista de ícones disponíveis na pasta assets/icons
        const iconFiles = [
          'gui.svg',
          'message-queue.svg',
          'cloud--alerting.svg',
          'application--web.svg',
          'cics--program.svg', 
          'application--virtual.svg',
          'binding--02.svg',
          'bare-metal-server.svg',
          'ai-generate.svg',
          'assembly--cluster.svg',
          'ai-governance--lifecycle.svg',
          'cics--db2-connection.svg',
          'security-services.svg',
          'kubernetes--control-plane-node.svg',
          'gui--management.svg',
          'instance--virtual.svg',
          'cloud--data-ops.svg',
          'cics--sit-overrides.svg',
          'container--runtime.svg',
          'edge-node--alt.svg',
          'db2--database.svg'
        ];
        
        console.log(`Encontrados ${iconFiles.length} ícones locais`, iconFiles);
        setIcons(iconFiles);
      } catch (error) {
        console.error("Erro ao buscar ícones:", error);
        setError("Não foi possível carregar os ícones. Por favor, tente novamente mais tarde.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchIcons();
  }, []);

  const fetchTechIcons = async () => {
    if (techIcons.length > 0) {
      console.log("Ícones de tecnologia já carregados:", techIcons.length);
      return;
    }
    
    try {
      setIsTechLoading(true);
      console.log("Carregando TODOS os ícones de tecnologia da pasta...");
      
      // Lista COMPLETA e EXATA dos ícones na pasta tech-icons
      const allTechIcons = [
        'adobe.svg', 'adobefirefly.svg', 'ae.svg', 'affinity.svg', 'affinitydesigner.svg',
        'affinityphoto.svg', 'affinitypublisher.svg', 'ai.svg', 'ai360.svg', 'aihubmix.svg',
        'aimass.svg', 'aionlabs.svg', 'airtable.svg', 'aistudio.svg', 'alacritty.svg',
        'algolia.svg', 'algorand.svg', 'alibabacloud.svg', 'alpinejs.svg', 'amp.svg',
        'amznwebserv.svg', 'analytics.svg', 'android.svg', 'angular.svg', 'angular17.svg',
        'animaapp.svg', 'antd.svg', 'antgroup.svg', 'anthropic.svg', 'anyscale.svg',
        'apache.svg', 'appwrite.svg', 'arc.svg', 'assemblyai.svg', 'astro.svg',
        'atlassian.svg', 'atom.svg', 'augment.svg', 'auth0.svg', 'automatic.svg',
        'avajs.svg', 'aws.svg', 'axiom.svg', 'axm.svg', 'axure.svg', 'aya.svg',
        'azure.svg', 'azureai.svg', 'babel.svg', 'backbonejs.svg', 'baichuan.svg',
        'baidu.svg', 'baiducloud.svg', 'bash.svg', 'bedrock.svg', 'bing.svg',
        'bitbucket.svg', 'bitnami.svg', 'blueprintjs.svg', 'bolt.svg', 'bootstrap4.svg',
        'bootstrap5.svg', 'brave.svg', 'bulmaui.svg', 'bunjs.svg', 'bytedance.svg',
        'c#.svg', 'c++.svg', 'cakephp.svg', 'canva.svg', 'cassandradb.svg',
        'centml.svg', 'cerebras.svg', 'chakraui.svg', 'chatglm.svg', 'chrome.svg',
        'chromium.svg', 'circleci.svg', 'civitai.svg', 'claude.svg', 'clickhouse.svg',
        'cline.svg', 'clojure.svg', 'cloudflare.svg', 'cloudinary.svg', 'codefresh.svg',
        'codegeex.svg', 'codeigniter.svg', 'cogvideo.svg', 'cogview.svg', 'cohere.svg',
        'colab.svg', 'comfyui.svg', 'commanda.svg', 'copilotgithub.svg', 'copilotms.svg',
        'coqui.svg', 'crewai.svg', 'crusoe.svg', 'crystal.svg', 'csharp.svg',
        'css3.svg', 'cypress.svg', 'dalle.svg', 'dart.svg', 'datadog.svg',
        'dbrx.svg', 'deepinfra.svg', 'deepmind.svg', 'deepseek.svg', 'deno.svg',
        'digitalocean.svg', 'django.svg', 'dlang.svg', 'doc2x.svg', 'docker.svg',
        'docsearch.svg', 'docusaurus.svg', 'doubao.svg', 'dovetail.svg', 'ec2.svg',
        'edge.svg', 'elastic.svg', 'electron.svg', 'elementui.svg', 'elixir.svg',
        'ember.svg', 'erlang.svg', 'esbuild.svg', 'eslint.svg', 'exa.svg',
        'expressjs.svg', 'fastgpt.svg', 'faunadb.svg', 'featherless.svg', 'figma.svg',
        'firebase.svg', 'firefox.svg', 'fireworks.svg', 'flask.svg', 'flutter.svg',
        'flutterflow.svg', 'flyio.svg', 'framer.svg', 'gatsby.svg', 'gcloud.svg',
        'gemini.svg', 'gemma.svg', 'git.svg', 'github.svg', 'gitlab.svg',
        'go.svg', 'google.svg', 'gradio.svg', 'grafana.svg', 'grafbase.svg',
        'graphite.svg', 'graphql.svg', 'greptile.svg', 'gridsome.svg', 'gruntjs.svg',
        'gsap.svg', 'gulp.svg', 'hailuo.svg', 'haskell.svg', 'headlessui.svg',
        'heroku.svg', 'hexo.svg', 'hhvm.svg', 'higress.svg', 'homebrew.svg',
        'hotjar.svg', 'html5.svg', 'huggingface.svg', 'hunyuan.svg', 'hyper.svg',
        'hyper2.svg', 'hyperbolic.svg', 'i18next.svg', 'id.svg', 'ie.svg',
        'iflytekcloud.svg', 'infermatic.svg', 'infinigence.svg', 'inkspace.svg', 'insomnia.svg',
        'internlm.svg', 'invision.svg', 'ionic.svg', 'jamstack.svg', 'java.svg',
        'jest.svg', 'jimeng.svg', 'jina.svg', 'jira.svg', 'jquery.svg',
        'js.svg', 'jslint.svg', 'json.svg', 'kibana.svg', 'kimi.svg',
        'kling.svg', 'kluster.svg', 'kotlin.svg', 'kubernetes.svg', 'langchain.svg',
        'langfuse.svg', 'langgraph.svg', 'langsmith.svg', 'laravel.svg', 'leptonai.svg',
        'less.svg', 'lightroom.svg', 'linux.svg', 'lit.svg', 'liveblocks.svg',
        'livekit.svg', 'llamaindex.svg', 'llava.svg', 'logrocket.svg', 'lokalise.svg',
        'lovable.svg', 'lua.svg', 'luma.svg', 'lunacy.svg', 'make.svg',
        'mariadb.svg', 'marionette.svg', 'markdown.svg', 'materialui.svg', 'meta-1.svg',
        'meta.svg', 'metaai.svg', 'microsoft-1.svg', 'microsoft.svg', 'minimax.svg',
        'miro.svg', 'mistral.svg', 'mochajs.svg', 'modelscope.svg', 'mongodb.svg',
        'mongoose.svg', 'monica.svg', 'mozilla.svg', 'myshell.svg', 'mysql.svg',
        'n8n.svg', 'neovim.svg', 'nestjs.svg', 'net.svg', 'netcore.svg',
        'netlify.svg', 'netlify2.svg', 'nextjs.svg', 'nextjs2.svg', 'nim.svg',
        'nitro.svg', 'nodejs.svg', 'notion.svg', 'nova.svg', 'novita.svg',
        'nplcloud.svg', 'npm.svg', 'npm2.svg', 'nuxtjs.svg', 'nvidia.svg',
        'ocaml.svg', 'onedrive.svg', 'openai.svg', 'openchat.svg', 'opera.svg',
        'oracle.svg', 'palm.svg', 'pandacss.svg', 'perplexity.svg', 'phidata.svg',
        'php.svg', 'pinia.svg', 'pixijs.svg', 'pixverse.svg', 'playwright.svg',
        'poe.svg', 'postgresql.svg', 'postman.svg', 'powershell.svg', 'ppio.svg',
        'preact.svg', 'presto.svg', 'prettier.svg', 'prisma.svg', 'ps.svg',
        'pugjs.svg', 'pwa.svg', 'pydanticai.svg', 'python.svg', 'pytorch.svg',
        'pytorch3d.svg', 'qingyan.svg', 'qiniu.svg', 'qwen.svg', 'qwik.svg',
        'radixui.svg', 'rails.svg', 'railway.svg', 'react.svg', 'reactquery.svg',
        'reactrouter.svg', 'redhat.svg', 'redis.svg', 'redux.svg', 'reduxsaga.svg',
        'remix.svg', 'render.svg', 'replit.svg', 'resend.svg', 'rollup.svg',
        'roocode.svg', 'rsshub.svg', 'ruby.svg', 'rust.svg', 'rwkv.svg',
        'safari.svg', 'sambanova.svg', 'sass.svg', 'scala.svg', 'search1api.svg',
        'semanticui.svg', 'sensenova.svg', 'serverless.svg', 'shadcnui.svg', 'siliconcloud.svg',
        'sketch.svg', 'sketch2.svg', 'slack.svg', 'snowflake.svg', 'solidity.svg',
        'solidjs.svg', 'spark.svg', 'spring.svg', 'stability.svg', 'statecloud.svg',
        'stepfun.svg', 'storyblok.svg', 'storybook.svg', 'strada.svg', 'stream.svg',
        'streamlit.svg', 'stylelint.svg', 'sublime.svg', 'supabase.svg', 'sveltejs.svg',
        'swagger.svg', 'swift.svg', 'tailwindcss.svg', 'targon.svg', 'tencent.svg',
        'tencentcloud.svg', 'threejs.svg', 'tiangong.svg', 'tii.svg', 'tinyhttp.svg',
        'together.svg', 'tripo.svg', 'tRPC.svg', 'typescript.svg', 'ubuntu.svg',
        'udio.svg', 'unjs.svg', 'unstructured.svg', 'upstage.svg', 'ur.svg',
        'v0.svg', 'vercel.svg', 'vertexai.svg', 'vidu.svg', 'vim.svg',
        'visualbasic.svg', 'vitejs.svg', 'vitest.svg', 'vllm.svg', 'volcengine.svg',
        'voyage.svg', 'vscode.svg', 'vuejs.svg', 'webassembly.svg', 'webpack.svg',
        'wenxin.svg', 'windsurf.svg', 'wordpress.svg', 'workersai.svg', 'xamarin.svg',
        'xd.svg', 'xinference.svg', 'xuanyuan.svg', 'yi.svg', 'yoga.svg',
        'yuanbao.svg', 'zapier.svg', 'zeabur.svg', 'zhipu.svg', 'zod.svg'
      ];
      
      console.log(`🎉 Carregando ${allTechIcons.length} ícones de tecnologia!`);
      setTechIcons(allTechIcons);
      
    } catch (error) {
      console.error("Erro ao carregar ícones de tecnologia:", error);
    } finally {
      setIsTechLoading(false);
    }
  };

  const getIconUrl = (iconName: string, isTech: boolean = false) => {
    const folder = isTech ? 'tech-icons' : 'icons';
    console.log(`Obtendo URL para o ícone: ${iconName} na pasta ${folder}`);
    return `/assets/${folder}/${iconName}`;
  };

  const filterIcons = (iconList: string[], searchTerm: string) => {
    if (!searchTerm.trim()) return iconList;
    return iconList.filter(icon => 
      icon.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const renderIconGrid = (iconList: string[], isTech: boolean = false) => {
    if (iconList.length === 0) {
      return <div className="text-muted-foreground text-sm">Nenhum ícone encontrado.</div>;
    }

    return (
      <div className="grid grid-cols-6 gap-2">
        {iconList.map((iconName) => {
          const fullPath = isTech ? `tech-icons/${iconName}` : `icons/${iconName}`;
          const isSelected = selectedIcon === fullPath;
          const iconUrl = getIconUrl(iconName, isTech);
          
          return (
            <div
              key={iconName}
              className={`
                p-2 rounded-md cursor-pointer transition-colors flex items-center justify-center
                ${isSelected ? 'bg-[#75e6da]/20 border-2 border-[#189ab4]' : 'border border-gray-200 hover:bg-gray-100'}
              `}
              onClick={() => onSelectIcon(fullPath)}
              title={iconName}
            >
              <img 
                src={iconUrl} 
                alt={iconName} 
                className="h-8 w-8 object-contain"
                onError={(e) => {
                  console.error(`Falha ao carregar ícone: ${iconName}, URL: ${iconUrl}`);
                  (e.target as HTMLImageElement).src = '/placeholder.svg';
                }}
              />
            </div>
          );
        })}

        {/* Opção para limpar a seleção de ícone - apenas na primeira aba */}
        {!isTech && (
          <div
            className={`
              p-2 rounded-md cursor-pointer transition-colors flex items-center justify-center
              ${!selectedIcon ? 'bg-[#75e6da]/20 border-2 border-[#189ab4]' : 'border border-gray-200 hover:bg-gray-100'}
            `}
            onClick={() => onSelectIcon('')}
            title="Sem ícone"
          >
            <div className="h-8 w-8 flex items-center justify-center text-gray-500 text-xs">
              Nenhum
            </div>
          </div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-6 gap-2">
        {[...Array(12)].map((_, index) => (
          <Skeleton key={index} className="h-10 w-10" />
        ))}
      </div>
    );
  }

  if (error) {
    return <div className="text-destructive text-sm">{error}</div>;
  }

  const filteredSimpleIcons = filterIcons(icons, searchSimple);
  const filteredTechIcons = filterIcons(techIcons, searchTech);

  return (
    <Tabs 
      defaultValue="simple" 
      className="w-full"
      onValueChange={(value) => {
        console.log("Mudando para aba:", value);
        if (value === "tech") {
          fetchTechIcons();
        }
      }}
    >
      <TabsList className="grid w-full grid-cols-2">
        <TabsTrigger value="simple">Ícones Simples ({icons.length})</TabsTrigger>
        <TabsTrigger value="tech">Tecnologias ({techIcons.length})</TabsTrigger>
      </TabsList>
      
      <TabsContent value="simple" className="space-y-3">
        <div className="relative">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar ícones simples..."
            value={searchSimple}
            onChange={(e) => setSearchSimple(e.target.value)}
            className="pl-8"
          />
        </div>
        {renderIconGrid(filteredSimpleIcons, false)}
      </TabsContent>
      
      <TabsContent value="tech" className="space-y-3">
        <div className="relative">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar tecnologias..."
            value={searchTech}
            onChange={(e) => setSearchTech(e.target.value)}
            className="pl-8"
          />
        </div>
        {isTechLoading ? (
          <div className="grid grid-cols-6 gap-2">
            {[...Array(12)].map((_, index) => (
              <Skeleton key={index} className="h-10 w-10" />
            ))}
          </div>
        ) : (
          renderIconGrid(filteredTechIcons, true)
        )}
      </TabsContent>
    </Tabs>
  );
}
